import React, { useState } from 'react';
import { View } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@shared/components/md-design-system/Layout/MainLayout';
import { Head } from '@shared/components/head';
import { Text } from '@shared/components/text';
import Button from '@shared/components/md-design-system/Button/Button';
import { OptionCard } from '@shared/components/md-design-system/Button/OptionCard';
import type { TNavigationStackWithParams } from 'app/types';
import { usePost } from 'app/queryProvider';
import { useQueryClient } from '@tanstack/react-query';
import { Telemetry } from 'app/services/telemetry/Telemetry';

type WhoMeasuredParams = { measurementId: string; vitalParameterName?: string };
type WhoMeasuredScreenRouteProp = RouteProp<TNavigationStackWithParams<WhoMeasuredParams>, 'WhoMeasured'>;
type WhoMeasuredScreenNavigationProp = NativeStackNavigationProp<TNavigationStackWithParams<any>>;

type UserResponseType = 'me' | 'someone_else' | '';

export const WhoMeasuredScreen = () => {
  const [userResponse, setUserResponse] = useState<UserResponseType>('');
  const navigation = useNavigation<WhoMeasuredScreenNavigationProp>();
  const route = useRoute<WhoMeasuredScreenRouteProp>();
  const { measurementId, vitalParameterName } = route.params;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: confirmMeasurement, isPending: isConfirming } = usePost<
    { measurementId: string; isPatientMeasurement: boolean },
    void
  >({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [['pathway', 'activities']] });
        navigation.navigate('RemeasurementSuccess');
      },
      onError: (error) => {
        Telemetry.error(error, { event: 'remeasurement.confirm.fail', domain: 'remeasurement' });
        navigation.navigate('RemeasurementSuccess');
      },
    },
  });

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleOptionPress = (value: UserResponseType) => {
    setUserResponse(value);
  };

  const handleButtonPress = () => {
    if (userResponse === 'me') {
      navigation.navigate('RemeasurementStart', {
        measurementId,
        vitalParameterName,
      });
    } else if (userResponse === 'someone_else') {
      confirmMeasurement({
        resource: { name: 'pathways' },
        url: '/patient-confirm-measurement',
        data: { measurementId, isPatientMeasurement: false },
      });
    }
  };

  const isButtonDisabled = !userResponse || isConfirming;

  return (
    <MainLayout navigation={handleGoBack}>
      <View className="flex-1 justify-between py-6">
        <View className="gap-6">
          <View className="gap-4">
            <Head type="h2" className="">
              {t('screens.remeasurement.questionnaire.header')}
            </Head>
            <Text className="text-black">
              {t('screens.remeasurement.questionnaire.description')}
            </Text>
          </View>

          <View className="gap-3">
            <OptionCard
              title={t('screens.remeasurement.questionnaire.me')}
              isSelected={userResponse === 'me'}
              onPress={() => handleOptionPress('me')}
            />
            <OptionCard
              title={t('screens.remeasurement.questionnaire.not_me')}
              isSelected={userResponse === 'someone_else'}
              onPress={() => handleOptionPress('someone_else')}
            />
          </View>
        </View>

        <View className="pt-6">
          <Button
            variant="primary"
            size="large"
            disabled={isButtonDisabled}
            onPress={handleButtonPress}
          >
            {t('buttons.continue')}
          </Button>
        </View>
      </View>
    </MainLayout>
  );
};

export default WhoMeasuredScreen;
